import React, { useState, useEffect } from 'react';
import { activitiesAPI, clientsAPI } from '../utils/api';
import { Plus, Filter, Calendar } from 'lucide-react';
import ActivityModal from '../components/modals/ActivityModal';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [filterClient, setFilterClient] = useState('');

  useEffect(() => {
    fetchData();
  }, [filterClient]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterClient) params.client_id = filterClient;
      const [activitiesRes, clientsRes] = await Promise.all([
        activitiesAPI.getAll(params),
        clientsAPI.getAll()
      ]);
      setActivities(activitiesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatTime = (date) => new Date(date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});

  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client?.client_name || 'Unknown';
  };

  const getOutcomeColor = (outcome) => {
    const colors = {
      'Positive': 'bg-green-100 text-green-700',
      'Neutral': 'bg-slate-100 text-slate-700',
      'Negative': 'bg-red-100 text-red-700',
      'Pending': 'bg-yellow-100 text-yellow-700'
    };
    return colors[outcome] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Activities</h1>
          <p className="text-slate-500 mt-1">Track all client interactions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Plus size={20} />
          Log Activity
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <select
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className="w-64 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Clients</option>
          {clients.map(c => (
            <option key={c._id} value={c._id}>{c.client_name}</option>
          ))}
        </select>
      </div>

      <div className="space-y-4">
        {activities.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">No activities logged yet</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity._id} className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded">
                      {activity.activity_type}
                    </span>
                    <span className={`px-3 py-1 text-sm font-medium rounded ${getOutcomeColor(activity.outcome)}`}>
                      {activity.outcome}
                    </span>
                    <span className="text-sm text-slate-500">
                      {formatDate(activity.date)} at {formatTime(activity.date)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{activity.subject}</h3>
                  <p className="text-sm text-slate-600 mb-3">{getClientName(activity.client_id)}</p>
                  {activity.notes && (
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{activity.notes}</p>
                  )}
                  {activity.next_action && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-sm font-medium text-yellow-900">Next Action:</p>
                      <p className="text-sm text-yellow-800">{activity.next_action}</p>
                      {activity.next_action_date && (
                        <p className="text-xs text-yellow-700 mt-1">Due: {formatDate(activity.next_action_date)}</p>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedActivity(activity);
                    setShowCreateModal(true);
                  }}
                  className="text-sm text-primary hover:text-primary/80 ml-4"
                >
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <ActivityModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedActivity(null);
          }}
          activity={selectedActivity}
          onSuccess={() => {
            fetchData();
            setShowCreateModal(false);
            setSelectedActivity(null);
          }}
        />
      )}
    </div>
  );
};

export default Activities;